import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useLanguage, SupportedLang } from '../../hooks/useLanguage';
import { useUpdateProfileMutation } from '../../store/api/userApi';
import { isAuthenticated } from '../../utils/tokenManager';

const LANG_OPTIONS: { code: SupportedLang; label: string; flag: string }[] = [
  { code: 'ar', label: 'العربية', flag: '🇸🇦' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'yo', label: 'Yorùbá', flag: '🇳🇬' },
  { code: 'ha', label: 'Hausa', flag: '🇳🇬' },
  { code: 'hi', label: 'हिन्दी', flag: '🇮🇳' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'bn', label: 'বাংলা', flag: '🇧🇩' },
];

export function LanguageSwitcher() {
  const { t } = useTranslation();
  const { currentLanguage, changeLanguage } = useLanguage();
  const [updateProfile] = useUpdateProfileMutation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const select = (next: SupportedLang) => {
    setOpen(false);
    const previous = currentLanguage;
    changeLanguage(next);
    if (!isAuthenticated()) return;
    (async () => {
      try {
        await updateProfile({ language: next }).unwrap();
      } catch {
        changeLanguage(previous);
        toast.error(t('navigation.languageSaveError'));
      }
    })();
  };

  const active = LANG_OPTIONS.find((o) => o.code === currentLanguage) || LANG_OPTIONS[0];

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="relative inline-flex items-center justify-center gap-1 w-14 h-9 rounded-lg bg-neutral-dark/40 text-neutral-light border border-neutral-light/10 transition-all duration-200 hover:bg-neutral-dark/60 hover:border-brand-accent/30 focus:outline-none focus:ring-2 focus:ring-brand-accent/40"
        aria-label={t('navigation.language')}
      >
        <span className="text-base leading-none">{active.flag}</span>
        <span className="font-heading font-semibold text-xs uppercase tracking-wider">
          {active.code.toUpperCase()}
        </span>
      </button>
      {open && (
        <div className="absolute end-0 top-full mt-2 w-48 animate-slide-down rounded-lg card-glass shadow-2xl overflow-hidden z-50">
          {LANG_OPTIONS.map((lang) => (
            <button
              key={lang.code}
              type="button"
              onClick={() => select(lang.code)}
              className={`flex w-full items-center gap-3 px-4 py-3 text-start text-sm transition-colors duration-200 ${
                lang.code === currentLanguage
                  ? 'bg-brand-accent/20 text-brand-accent'
                  : 'text-neutral-light hover:bg-brand-accent/10 hover:text-brand-accent'
              }`}
            >
              <span className="text-base leading-none">{lang.flag}</span>
              <span>{lang.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
